import { forwardRef, useContext, MouseEvent } from "react";
import { StepperContext } from "./Stepper";
import { Button, ButtonProps } from "../Button";

/**
 * Props for the StepperPrevButton component
 * @extends ButtonProps
 */
export type StepperPrevButtonProps = Omit<ButtonProps, "children"> & {
  /**
   * Label for the previous button.
   * @default "Previous"
   */
  label?: string;
};

/**
 * Previous button component for navigating to the previous step in the Stepper.
 *
 * Features:
 * - Navigates to the previous step when available
 * - Hides when on the first step
 * - Restores navigation when returning from completion
 * - Accessible with proper button semantics
 * - Customizable label and styling
 * - Integration with stepper context
 * - Automatic focus management
 * - Support for completion state handling
 *
 * @example
 * <Stepper.PrevButton label="Go Back" />
 *
 * @example
 * <Stepper.PrevButton
 *   label="Previous Step"
 *   appearance="secondary"
 *   onClick={(e) => console.log('Going back')}
 * />
 */
export const StepperPrevButton = forwardRef<
  HTMLButtonElement,
  StepperPrevButtonProps
>((props, ref) => {
  const {
    setCurrent,
    setFocus,
    allCompleted,
    setAllCompleted,
    items,
    current,
    allowNavigateToPrevStep,
  } = useContext(StepperContext);
  const { onClick, label = "Previous", onFocus, ...rest } = props;

  const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    if (allCompleted) {
      setAllCompleted?.(false);
      if (allowNavigateToPrevStep) {
        items?.[current].setAttribute("tabindex", "0");
      }
    } else {
      setCurrent(current - 1);
      setFocus(current - 1);
    }

    onClick?.(e);
  };

  return current === 0 ? null : (
    <Button
      data-anv="stepper-prev-button"
      onClick={onClickHandler}
      ref={ref}
      {...rest}
    >
      {label}
    </Button>
  );
});

StepperPrevButton.displayName = "StepperPrevButton";
